<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

const pickQrValue = response => {
  if (!response) return null;
  if (typeof response === 'string') return response;

  return (
    response.qrcode ||
    response.base64 ||
    response.code ||
    pickQrValue(response.evolution) ||
    pickQrValue(response.connect) ||
    pickQrValue(response.instance) ||
    null
  );
};

const qrImageSource = value => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (value.startsWith('data:image')) return value;
    if (/^[A-Za-z0-9+/=]+$/.test(value.slice(0, 80))) {
      return `data:image/png;base64,${value}`;
    }
    return '';
  }
  return qrImageSource(value.base64 || value.qrcode || value.code);
};

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      isCreating: false,
      isRefreshing: false,
      instanceName: '',
      inbox: null,
      qrCode: null,
      rawQrCode: '',
      qrRefreshTimer: null,
      connectionState: '',
    };
  },
  computed: {
    qrImage() {
      return qrImageSource(this.qrCode);
    },
    hasQrCode() {
      return Boolean(this.qrImage || this.rawQrCode);
    },
    accountId() {
      return this.$route.params.accountId;
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
  },
  beforeUnmount() {
    this.stopQrRefreshLoop();
  },
  methods: {
    applyEvolutionResponse(response) {
      this.instanceName = response.instance_name || this.instanceName;
      this.inbox = response.inbox || this.inbox;
      this.connectionState =
        response.state ||
        response.evolution?.state ||
        response.evolution?.instance?.state ||
        this.connectionState;

      const qrCode = pickQrValue(response);
      if (qrCode) {
        this.qrCode = qrCode;
      }

      this.rawQrCode = this.qrImage ? '' : this.rawFallbackValue(this.qrCode);
    },
    rawFallbackValue(value) {
      if (!value || (typeof value === 'object' && !Object.keys(value).length)) {
        return '';
      }

      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    },
    startQrRefreshLoop() {
      this.stopQrRefreshLoop();
      if (!this.instanceName) return;

      this.qrRefreshTimer = window.setInterval(() => {
        if (this.connectionState === 'open') {
          this.stopQrRefreshLoop();
          return;
        }

        this.refreshQrCode({ silent: true });
      }, 30000);
    },
    stopQrRefreshLoop() {
      if (!this.qrRefreshTimer) return;

      window.clearInterval(this.qrRefreshTimer);
      this.qrRefreshTimer = null;
    },
    async createEvolutionSource() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isCreating = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/aivio/evolution_inboxes`,
          {
            name: this.inboxName?.trim(),
            phone: this.phoneNumber,
          }
        );
        this.applyEvolutionResponse(data);
        this.startQrRefreshLoop();
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      } finally {
        this.isCreating = false;
      }
    },
    async refreshQrCode({ silent = false } = {}) {
      if (!this.instanceName) return;

      this.isRefreshing = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/aivio/evolution_inboxes/${encodeURIComponent(
            this.instanceName
          )}/connect`
        );
        this.applyEvolutionResponse(data);
      } catch (error) {
        if (!silent) {
          useAlert(
            error?.response?.data?.message ||
              this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
          );
        }
      } finally {
        this.isRefreshing = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createEvolutionSource()">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            placeholder="+996"
            @blur="v$.phoneNumber.$touch"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
        <p class="help-text">
          Номер используется только для подписи instance в Evolution. QR нужно
          отсканировать в WhatsApp на телефоне.
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isCreating"
          type="submit"
          solid
          blue
          label="Создать источник и показать QR"
        />
      </div>
    </form>

    <div
      v-if="instanceName"
      class="p-5 rounded-xl border border-n-weak bg-n-alpha-2"
    >
      <div class="mb-4">
        <h2 class="mb-1 text-base font-medium text-n-slate-12">
          QR-код для подключения WhatsApp
        </h2>
        <p class="text-sm text-n-slate-11">
          Откройте WhatsApp на телефоне, выберите связанные устройства и
          отсканируйте QR-код.
        </p>
      </div>

      <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div
          class="flex items-center justify-center w-64 h-64 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <img
            v-if="qrImage"
            :src="qrImage"
            alt="Evolution WhatsApp QR"
            class="block w-56 h-56 rounded-lg"
          />
          <textarea
            v-else-if="rawQrCode"
            class="w-56 h-40 text-xs"
            readonly
            :value="rawQrCode"
          />
          <span v-else class="text-sm text-n-slate-11">
            QR пока не получен
          </span>
        </div>

        <div class="flex flex-col gap-3 min-w-0">
          <div class="text-sm text-n-slate-11">
            Instance:
            <span class="font-medium text-n-slate-12">{{ instanceName }}</span>
          </div>
          <div v-if="inbox" class="text-sm text-n-slate-11">
            Inbox:
            <span class="font-medium text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <div class="flex flex-wrap gap-3 mt-1">
            <NextButton
              :is-loading="isRefreshing"
              type="button"
              label="Обновить QR"
              @click="refreshQrCode"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
